// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

export interface OrderDownloadPdfInput {
  category: string;
  title: string;
  orderId: string;
  patientName: string;
  fields: { label: string; value: string }[];
}

export async function downloadOrderClassTemplate(input: OrderDownloadPdfInput): Promise<void> {
  const [pdfMakeModule, pdfFontsModule] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  const pdfMake = pdfMakeModule.default;
  const pdfFonts = pdfFontsModule.default;
  const pdfMakeRuntime = pdfMake as typeof pdfMake & {
    addVirtualFileSystem?: (fonts: Record<string, string>) => void;
    vfs?: Record<string, string>;
  };
  const fonts = pdfFonts as unknown as Record<string, string>;
  if (pdfMakeRuntime.addVirtualFileSystem) {
    pdfMakeRuntime.addVirtualFileSystem(fonts);
  } else {
    pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? fonts;
  }
  const safeId = input.orderId
    .replaceAll(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  pdfMake
    .createPdf({
      content: [
        { text: 'Polaris Sleep', style: 'brand' },
        { text: `${input.category} order`, style: 'eyebrow' },
        { text: input.title, style: 'title' },
        { text: `Patient: ${input.patientName}`, margin: [0, 0, 0, 4] },
        { text: `Order ID: ${input.orderId}`, margin: [0, 0, 0, 16] },
        {
          table: {
            widths: ['auto', '*'],
            body: input.fields.map((field) => [{ text: field.label, bold: true, color: '#555555' }, field.value]),
          },
          layout: 'lightHorizontalLines',
        },
        { text: 'Class-level order form generated from the order resource.', style: 'footer' },
      ],
      styles: {
        brand: { fontSize: 18, bold: true, color: '#640040', margin: [0, 0, 0, 12] },
        eyebrow: { fontSize: 10, color: '#666666', bold: true, uppercase: true },
        title: { fontSize: 20, bold: true, margin: [0, 4, 0, 10] },
        footer: { fontSize: 9, color: '#777777', margin: [0, 24, 0, 0] },
      },
      defaultStyle: { fontSize: 10 },
    })
    .download(`${safeId || 'order'}.pdf`);
}
